import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { NetworkBadge } from "./NetworkBadge";
import { WalletChip } from "./WalletChip";
import type { PrimaryView } from "./routing";
import { useAdminCapability } from "./useAdminCapability";
import { useModerationNotifications } from "./useModerationNotifications";
import { NotificationBell } from "./NotificationBell";
import { cn } from "./cn";
import { useI18n, LanguageSelector } from "../i18n";
import gridLogo from "../assets/grid-logo-full.png";
import gridLogoIcon from "../assets/grid-logo.png";
import { scrollToGridAligned } from "./gridAnchor";

export interface TopNavigationProps {
  activeView: PrimaryView;
  narrative?: boolean;
}

/**
 * Shared primary navigation (Macro #26 · Increment 3).
 *
 * Two kinds of destinations coexist WITHOUT a header redesign:
 *   - ANCHOR items (Overview → #hero, Territory → #grid) scroll the frozen Home
 *     when the reader is on the narrative page, and are plain hash links from a
 *     dedicated route page.
 *   - ROUTE items (Explore, How It Works, Creators & Brands, About) are the four
 *     new dedicated public pages. "Roadmap" is intentionally NO LONGER a public
 *     nav voice — it is replaced by "How It Works".
 * The Home body is never touched; only this shared bar exposes the new pages.
 */
type NavItem =
  | { kind: "anchor"; id: string; labelKey: string; testid: string }
  | { kind: "route"; route: string; view: PrimaryView; labelKey: string; testid: string };

const NAV_ITEMS: NavItem[] = [
  { kind: "anchor", id: "hero", labelKey: "nav.overview", testid: "nav-overview" },
  { kind: "route", route: "about", view: "about", labelKey: "nav.about", testid: "nav-about" },
  { kind: "route", route: "how-it-works", view: "how_it_works", labelKey: "nav.howItWorks", testid: "nav-how-it-works" },
  { kind: "route", route: "explore", view: "explore", labelKey: "nav.explore", testid: "nav-explore" },
  { kind: "route", route: "creators", view: "creators", labelKey: "nav.creators", testid: "nav-creators" },
  { kind: "route", route: "marketplace", view: "marketplace", labelKey: "nav.marketplace", testid: "nav-marketplace" },
  { kind: "route", route: "rent", view: "rent", labelKey: "nav.rent", testid: "nav-rent" },
  { kind: "route", route: "feed", view: "feed", labelKey: "nav.feed", testid: "nav-feed" },
];

const ANCHOR_IDS = NAV_ITEMS.filter((i): i is Extract<NavItem, { kind: "anchor" }> => i.kind === "anchor").map((i) => i.id);

/**
 * TopNavigation — WO#5 Neon-Editorial v2 (per Claudio's stealth
 * aerospace reference).
 *
 * Slim 56px bar. Left: square G-logo mark + wordmark "THE GRID"
 * letter-spaced. Center: uppercase real destinations; the current
 * scroll section gets a solid teal underline (see IntersectionObserver
 * below). Right: NetworkBadge + WalletChip + outlined coral/teal
 * "ENTER THE GRID" pill (scrolls to #grid — anti-fake: never
 * triggers SIWE). Hamburger drawer holds the same destinations for
 * mobile / power-users.
 */
export function TopNavigation({
  activeView,
  narrative = true,
}: TopNavigationProps) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [currentSection, setCurrentSection] = useState<string>("hero");
  const { isAdmin } = useAdminCapability();
  const { ownerUnread, adminUnread } = useModerationNotifications();
  const { t } = useI18n();

  const closeDrawer = useCallback(() => setDrawerOpen(false), []);

  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeDrawer();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [drawerOpen, closeDrawer]);

  // Track scroll position → shadow the bar once the user leaves hero
  useEffect(() => {
    if (!narrative) return;
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [narrative]);

  // Active section highlight (IntersectionObserver) — never fake
  // active state for non-existent destinations.
  useEffect(() => {
    if (!narrative) return;
    const els = ANCHOR_IDS
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => !!el);
    if (els.length === 0) return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && entry.intersectionRatio > 0.35) {
            setCurrentSection(entry.target.id);
          }
        }
      },
      { rootMargin: "-40% 0px -50% 0px", threshold: [0.35, 0.6] },
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [narrative]);

  const scrollToAnchor = useCallback((id: string) => {
    if (id === "grid") return scrollToGridAligned();
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  const handleAnchor = (id: string) => (e: React.MouseEvent) => {
    if (!narrative) return;
    e.preventDefault();
    setDrawerOpen(false);
    scrollToAnchor(id);
  };

  return (
    <>
      <style>{`
        @keyframes gridHiwGlow {
          0%, 100% { text-shadow: 0 0 0 rgba(55,224,200,0); }
          50% { text-shadow: 0 0 11px rgba(55,224,200,0.9), 0 0 3px rgba(55,224,200,0.65); }
        }
        @keyframes gridHiwDot {
          0%, 100% { opacity: 0.35; transform: scale(0.85); box-shadow: 0 0 0 rgba(55,224,200,0); }
          50% { opacity: 1; transform: scale(1); box-shadow: 0 0 8px rgba(55,224,200,0.95); }
        }
        .grid-hiw-glow { animation: gridHiwGlow 2s ease-in-out infinite; }
        .grid-hiw-dot { animation: gridHiwDot 2s ease-in-out infinite; }
        @keyframes gridMktGlow {
          0%, 100% { text-shadow: 0 0 0 rgba(0,229,217,0); }
          50% { text-shadow: 0 0 13px rgba(0,229,217,0.95), 0 0 4px rgba(0,229,217,0.7); }
        }
        @keyframes gridMktDot {
          0%, 100% { opacity: 0.4; transform: scale(0.85); box-shadow: 0 0 0 rgba(0,229,217,0); }
          50% { opacity: 1; transform: scale(1); box-shadow: 0 0 9px rgba(0,229,217,1); }
        }
        .grid-mkt-glow { animation: gridMktGlow 2.4s ease-in-out infinite; }
        .grid-mkt-dot { animation: gridMktDot 2.4s ease-in-out infinite; }
        @keyframes gridRentGlow {
          0%, 100% { text-shadow: 0 0 0 rgba(255,71,87,0); }
          50% { text-shadow: 0 0 13px rgba(255,71,87,0.95), 0 0 4px rgba(255,71,87,0.7); }
        }
        @keyframes gridRentDot {
          0%, 100% { opacity: 0.4; transform: scale(0.85); box-shadow: 0 0 0 rgba(255,71,87,0); }
          50% { opacity: 1; transform: scale(1); box-shadow: 0 0 9px rgba(255,71,87,1); }
        }
        .grid-rent-glow { animation: gridRentGlow 2.4s ease-in-out infinite; }
        .grid-rent-dot { animation: gridRentDot 2.4s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) {
          .grid-hiw-glow, .grid-hiw-dot, .grid-mkt-glow, .grid-mkt-dot, .grid-rent-glow, .grid-rent-dot { animation: none; }
        }
      `}</style>
      <header
        data-testid="top-navigation"
        className={cn(
          "relative shrink-0",
          "flex items-center gap-3 md:gap-4",
          "h-12 px-4 md:px-8",
          "backdrop-blur-xl transition-[background-color,box-shadow,border-color] duration-med",
          scrolled
            ? "bg-[rgba(10,15,20,0.72)] border-b border-[color:var(--border-divider)] shadow-[0_12px_30px_-14px_rgba(0,0,0,0.95)]"
            : "bg-[rgba(10,15,20,0.30)] border-b border-transparent",
        )}
      >
        {/* Brand mark */}
        <a
          data-testid="brand-mark"
          href={narrative ? "#hero" : "#"}
          onClick={
            narrative
              ? (e) => {
                  e.preventDefault();
                  scrollToAnchor("hero");
                }
              : undefined
          }
          className="group flex items-center gap-3 no-underline text-text-primary select-none shrink-0"
          aria-label="THE GRID — Home"
        >
          <img
            src={gridLogoIcon}
            alt="THE GRID"
            data-testid="brand-logo-img"
            className={cn(
              "md:hidden h-7 w-auto object-contain select-none pointer-events-none",
              "transition-[filter] duration-med",
              "group-hover:drop-shadow-[0_0_12px_rgba(56,182,255,0.75)]",
            )}
            draggable={false}
          />
          <img
            src={gridLogo}
            alt="THE GRID"
            data-testid="brand-logo-full"
            className={cn(
              "hidden md:block h-7 w-auto object-contain select-none pointer-events-none",
              "transition-[filter] duration-med",
              "group-hover:drop-shadow-[0_0_12px_rgba(56,182,255,0.7)]",
            )}
            draggable={false}
          />
          {activeView === "purchase" && (
            <span
              data-testid="brand-purchase-badge"
              className="hidden sm:inline-flex items-center ml-3 h-5 px-2 rounded-sm font-display text-[9.5px] font-semibold tracking-[0.22em] uppercase text-neon-coral border border-border-coral"
            >
              {t("nav.purchaseBadge")}
            </span>
          )}
        </a>

        {/* Section-index tag — mirrors reference "02 | TERRITORY" */}
        {narrative && (() => {
          const anchors = NAV_ITEMS.filter((i): i is Extract<NavItem, { kind: "anchor" }> => i.kind === "anchor");
          const idx = anchors.findIndex((a) => a.id === currentSection);
          if (idx <= 0) return null; // hide on hero (idx 0) and unknown
          const active = anchors[idx];
          const num = String(idx + 1).padStart(2, "0");
          return (
            <span
              data-testid="section-index-tag"
              data-section-id={active.id}
              className="hidden 2xl:inline-flex section-index-tag"
              aria-live="polite"
            >
              <span className="idx-num">{num}</span>
              <span className="idx-sep">|</span>
              <span>{t(active.labelKey)}</span>
            </span>
          );
        })()}

        {/* Center rail — Overview · About · How It Works · Explore ·
            Creators & Brands · Territory. Shown on xl+; below that the hamburger
            drawer carries the SAME destinations + Profile / Enter the Grid / Sign
            In (never hidden mobile-only). */}
        <nav
          data-testid="top-nav-items"
          aria-label={t("nav.primaryAria")}
          className="hidden xl:flex items-center gap-2 2xl:gap-3.5 ml-3 shrink-0"
        >
          {NAV_ITEMS.map((item) => {
            const isActive =
              item.kind === "anchor"
                ? narrative && currentSection === item.id
                : !narrative && activeView === item.view;
            const isHiw = item.testid === "nav-how-it-works";
            const isMkt = item.testid === "nav-marketplace";
            const isRent = item.testid === "nav-rent";
            const href = item.kind === "anchor" ? `#${item.id}` : `#${item.route}`;
            const onClick = item.kind === "anchor" ? handleAnchor(item.id) : undefined;
            return (
              <a
                key={item.testid}
                href={href}
                data-testid={item.testid}
                data-active={isActive ? "true" : "false"}
                onClick={onClick}
                aria-current={isActive ? "page" : undefined}
                title={isHiw ? t("nav.howItWorksHintAria") : undefined}
                className={cn(
                  "relative no-underline whitespace-nowrap inline-flex items-center gap-1.5",
                  "font-mono text-[10px] 2xl:text-[11px] font-semibold tracking-[0.1em] 2xl:tracking-[0.14em] uppercase",
                  "transition-colors duration-fast",
                  isHiw
                    ? "text-neon-teal grid-hiw-glow"
                    : isMkt
                      ? "text-neon-teal grid-mkt-glow"
                      : isRent
                        ? "text-neon-coral grid-rent-glow"
                        : isActive
                          ? "text-text-primary"
                          : "text-text-secondary hover:text-text-primary",
                )}
              >
                {isHiw && (
                  <span aria-hidden className="grid-hiw-dot h-1.5 w-1.5 rounded-full bg-neon-teal shrink-0" />
                )}
                {isMkt && (
                  <span aria-hidden className="grid-mkt-dot h-1.5 w-1.5 rounded-full bg-neon-teal shrink-0" />
                )}
                {isRent && (
                  <span aria-hidden className="grid-rent-dot h-1.5 w-1.5 rounded-full bg-neon-coral shrink-0" />
                )}
                {item.testid === "nav-creators" ? t("nav.creatorsShort") : t(item.labelKey)}
                <span
                  aria-hidden
                  className={cn(
                    "absolute left-0 right-0 -bottom-1 h-px transition-all duration-med",
                    isRent ? "bg-neon-coral" : "bg-neon-teal",
                    isActive ? (isRent ? "opacity-100 shadow-glow-coral-sm" : "opacity-100 shadow-glow-teal-sm") : "opacity-0",
                  )}
                />
              </a>
            );
          })}
        </nav>

        {/* Right cluster */}
        <div className="ml-auto flex items-center gap-1.5 2xl:gap-2 shrink-0">
          {isAdmin && (
            <a
              data-testid="nav-admin-hud"
              href="#admin"
              aria-label={adminUnread ? "Admin Command Console — owner is waiting" : "Admin Command Console"}
              className={cn(
                "relative facet-btn facet-btn--coral",
                "hidden sm:inline-flex shrink-0 whitespace-nowrap",
                "h-7 px-2.5 2xl:px-3 no-underline",
                "text-[10px] font-semibold tracking-[0.18em]",
              )}
            >
              <svg width="12" height="12" viewBox="0 0 14 14" fill="none" aria-hidden>
                <path d="M7 1l4.5 1.8v3.4c0 2.9-1.9 4.9-4.5 5.8-2.6-.9-4.5-2.9-4.5-5.8V2.8L7 1z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
              </svg>
              [ Admin ]
              {adminUnread && <NotifDot testid="nav-admin-unread-dot" />}
            </a>
          )}
          <NotificationBell />
          <NetworkBadge />
          <LanguageSelector />
          <WalletChip />
          <a
            data-testid="nav-profile-btn"
            href="#profile"
            aria-label={ownerUnread ? "Profile — you have an unread message" : t("nav.profile")}
            className={cn(
              "relative hidden xl:inline-flex items-center gap-2 h-7 px-2.5 2xl:px-3 shrink-0 whitespace-nowrap",
              "font-mono text-[10px] font-medium tracking-[0.16em] uppercase",
              "text-text-primary hover:text-text-primary no-underline",
              "neon-glass pill-ghost",
            )}
          >
            <svg width="12" height="12" viewBox="0 0 14 14" fill="none" aria-hidden>
              <circle cx="7" cy="4.3" r="2.4" stroke="currentColor" strokeWidth="1.2" />
              <path d="M2.5 12c0-2.4 2-3.9 4.5-3.9s4.5 1.5 4.5 3.9" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
            </svg>
            <span className="hidden 2xl:inline">{t("nav.profile")}</span>
            {ownerUnread && <NotifDot testid="nav-profile-unread-dot" />}
          </a>
          <a
            data-testid="nav-enter-grid-btn"
            href="#grid"
            onClick={narrative ? handleAnchor("grid") : undefined}
            className={cn(
              "hidden xl:inline-flex items-center gap-2 h-7 px-2.5 2xl:px-3 shrink-0 whitespace-nowrap",
              "font-mono text-[10px] font-medium tracking-[0.16em] uppercase",
              "text-text-primary hover:text-text-primary no-underline",
              "neon-glass pill-ghost",
            )}
          >
            {t("nav.enterGrid")}
            <svg width="10" height="10" viewBox="0 0 12 12" fill="none" aria-hidden>
              <path d="M3 6h6m0 0L6 3m3 3L6 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </a>
          <button
            type="button"
            data-testid="nav-hamburger-btn"
            aria-label={ownerUnread ? "Open navigation menu — you have an unread message" : t("nav.openMenuAria")}
            aria-expanded={drawerOpen}
            aria-controls="nav-drawer"
            onClick={() => setDrawerOpen((v) => !v)}
            className={cn(
              "relative xl:hidden facet-btn h-7 w-7 shrink-0",
              "transition-colors duration-fast",
            )}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
              <path d="M2 3.5h10M2 7h10M2 10.5h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
            {ownerUnread && <NotifDot testid="nav-hamburger-unread-dot" />}
          </button>
        </div>
      </header>

      {drawerOpen && createPortal(
        <>
          <div
            data-testid="nav-drawer-backdrop"
            aria-hidden
            onClick={closeDrawer}
            className="fixed inset-0 z-modal bg-black/70 backdrop-blur-sm"
          />
          <aside
            id="nav-drawer"
            data-testid="nav-drawer"
            role="dialog"
            aria-label="Navigation"
            className={cn(
              "fixed top-0 right-0 z-modal h-screen w-[min(360px,86vw)] pt-[env(safe-area-inset-top,0px)]",
              "neon-glass-strong",
              "flex flex-col",
              "animate-[gridDrawerSlide_.28s_cubic-bezier(0.16,1,0.3,1)]",
            )}
          >
            <header className="flex items-center justify-between px-5 h-14 border-b border-border-divider">
              <span className="font-display text-[10.5px] font-semibold tracking-[0.24em] uppercase text-neon-teal">
                {t("nav.navigate")}
              </span>
              <button
                type="button"
                data-testid="nav-drawer-close-btn"
                aria-label={t("nav.closeMenuAria")}
                onClick={closeDrawer}
                className="h-8 w-8 inline-flex items-center justify-center rounded-md text-text-secondary hover:text-neon-teal hover:bg-elevated"
              >
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
                  <path d="M2 2l8 8M10 2l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </button>
            </header>
            <nav className="flex-1 overflow-y-auto py-4">
              {NAV_ITEMS.map((item, idx) => {
                const href = item.kind === "anchor" ? `#${item.id}` : `#${item.route}`;
                const isHiw = item.testid === "nav-how-it-works";
                const isMkt = item.testid === "nav-marketplace";
                const isRent = item.testid === "nav-rent";
                const onClick = (e: React.MouseEvent) => {
                  if (item.kind === "anchor" && narrative) {
                    e.preventDefault();
                    scrollToAnchor(item.id);
                  }
                  setDrawerOpen(false);
                };
                return (
                  <a
                    key={item.testid}
                    data-testid={`drawer-${item.testid}`}
                    href={href}
                    onClick={onClick}
                    className={cn(
                      "block px-6 py-4 no-underline",
                      "font-display text-[14px] font-semibold tracking-[0.10em]",
                      isHiw
                        ? "text-neon-teal grid-hiw-glow hover:bg-elevated"
                        : isMkt
                          ? "text-neon-teal grid-mkt-glow hover:bg-elevated"
                          : isRent
                            ? "text-neon-coral grid-rent-glow hover:bg-elevated"
                            : "text-text-primary hover:bg-elevated hover:text-neon-teal",
                      "border-b border-border-divider",
                      "transition-colors duration-fast",
                    )}
                  >
                    <span className="uppercase text-text-muted text-[9.5px] tracking-[0.26em] block mb-1 font-mono">
                      {String(idx + 1).padStart(2, "0")}
                    </span>
                    <span className="inline-flex items-center gap-2">
                      {isHiw && (
                        <span aria-hidden className="grid-hiw-dot h-1.5 w-1.5 rounded-full bg-neon-teal shrink-0" />
                      )}
                      {isMkt && (
                        <span aria-hidden className="grid-mkt-dot h-1.5 w-1.5 rounded-full bg-neon-teal shrink-0" />
                      )}
                      {isRent && (
                        <span aria-hidden className="grid-rent-dot h-1.5 w-1.5 rounded-full bg-neon-coral shrink-0" />
                      )}
                      {t(item.labelKey)}
                    </span>
                  </a>
                );
              })}
              <a
                data-testid="drawer-nav-profile"
                href="#profile"
                onClick={() => setDrawerOpen(false)}
                className={cn(
                  "block px-6 py-4 no-underline",
                  "font-display text-[14px] font-semibold tracking-[0.10em]",
                  "text-text-primary hover:bg-elevated hover:text-neon-teal",
                  "border-b border-border-divider",
                  "transition-colors duration-fast",
                )}
              >
                <span className="uppercase text-text-muted text-[9.5px] tracking-[0.26em] block mb-1 font-mono">
                  {String(NAV_ITEMS.length + 1).padStart(2, "0")}
                </span>
                {t("nav.profile")}
              </a>
              <a
                data-testid="drawer-nav-enter-grid"
                href="#grid"
                onClick={(e) => {
                  if (narrative) {
                    e.preventDefault();
                    scrollToAnchor("grid");
                  }
                  setDrawerOpen(false);
                }}
                className={cn(
                  "block px-6 py-4 no-underline",
                  "font-display text-[14px] font-semibold tracking-[0.10em]",
                  "text-text-primary hover:bg-elevated hover:text-neon-teal",
                  "border-b border-border-divider",
                  "transition-colors duration-fast",
                )}
              >
                <span className="uppercase text-text-muted text-[9.5px] tracking-[0.26em] block mb-1 font-mono">
                  {String(NAV_ITEMS.length + 2).padStart(2, "0")}
                </span>
                {t("nav.enterGrid")}
              </a>
              <a
                data-testid="drawer-nav-signin"
                href="#auth"
                onClick={() => setDrawerOpen(false)}
                className={cn(
                  "block px-6 py-4 no-underline",
                  "font-display text-[14px] font-semibold tracking-[0.10em]",
                  "text-text-primary hover:bg-elevated hover:text-neon-coral",
                  "border-b border-border-divider",
                )}
              >
                <span className="uppercase text-text-muted text-[9.5px] tracking-[0.26em] block mb-1 font-mono">
                  {String(NAV_ITEMS.length + 3).padStart(2, "0")}
                </span>
                {t("nav.signIn")}
              </a>
              {isAdmin && (
                <a
                  data-testid="drawer-nav-admin"
                  href="#admin"
                  onClick={() => setDrawerOpen(false)}
                  className={cn(
                    "flex items-center justify-between px-6 py-4 no-underline",
                    "font-display text-[14px] font-semibold tracking-[0.10em]",
                    "text-neon-coral hover:bg-elevated",
                    "border-b border-border-divider",
                    "transition-colors duration-fast",
                  )}
                >
                  <span>
                    <span className="uppercase text-text-muted text-[9.5px] tracking-[0.26em] block mb-1 font-mono">
                      {String(NAV_ITEMS.length + 4).padStart(2, "0")}
                    </span>
                    <span className="inline-flex items-center gap-2">
                      <svg width="12" height="12" viewBox="0 0 14 14" fill="none" aria-hidden>
                        <path d="M7 1l4.5 1.8v3.4c0 2.9-1.9 4.9-4.5 5.8-2.6-.9-4.5-2.9-4.5-5.8V2.8L7 1z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
                      </svg>
                      [ Admin ]
                    </span>
                  </span>
                  {adminUnread && <NotifDot testid="drawer-nav-admin-unread-dot" />}
                </a>
              )}
            </nav>
            <footer className="px-6 py-4 border-t border-border-divider text-[11px] text-text-muted font-mono">
              THE GRID · V1.0 Alpha
            </footer>
          </aside>
          <style>{`@keyframes gridDrawerSlide{from{transform:translateX(100%);opacity:0}to{transform:translateX(0);opacity:1}}`}</style>
        </>,
        document.body,
      )}
    </>
  );
}

/** Luminous unread-message indicator — a small pulsing coral dot pinned to the
 * top-right corner of a top-bar control (Macro #20). */
function NotifDot({ testid }: { testid: string }) {
  return (
    <span
      data-testid={testid}
      aria-hidden
      className="notif-glow-dot absolute -top-1 -right-1 h-2.5 w-2.5 pointer-events-none"
    />
  );
}
